// ================= IMPORTS =================
import React, { useMemo, useRef } from "react";
import ApiDropdown from "../../Components/DropDown/Apidropdown";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
  PieChart,
  Pie,
  Cell,
  LabelList,
} from "recharts";

import "./Reporting.css";

import html2canvas from "html2canvas";
import PptxGenJS from "pptxgenjs";

interface RepetitiveData {
  link: string;
  total: number;
  [key: string]: string | number;
}
const COLORS = [
  "#7861c6",

  "#648b60",
  "#320881",
  "rgb(122, 173, 142)",
  "#4832a4",
  "#7861c6",
  "#d761cf",
  "#9274ff",
  "#000000",
];
interface Props {
  periodFrom: string;
  periodTo: string;
  setPeriodFrom: (v: string) => void;
  setPeriodTo: (v: string) => void;

  module: string;
  setModule: (v: string) => void;

  customers: string[];
  setCustomers: (v: string[]) => void;

  modules: any[];
  handleGenerate: () => void;
  loading: boolean;

  complaints: any[];
  categories: any[];
  errorTypes: any[];
  status: any[];
  networkData: any[];

  openIncidents: any[];
  criticalIncidents: any[];
  closedOnceIncidents: any[];

  taskTypeData: any[];
  taskTable: any[];

  changeTable: any[];

  tasksNextTwoWeeks: any[];
  changeNextTwoWeeks: any[];
  problemData: any[];
  repetitive: any[];
}

// ================= COMPONENT =================
const ReportingView: React.FC<Props> = ({
  periodFrom,
  periodTo,
  setPeriodFrom,
  setPeriodTo,

  module,
  setModule,

  customers,
  setCustomers,

  modules,

  handleGenerate,
  loading,

  complaints,
  categories,
  errorTypes,
  status,
  networkData,

  openIncidents,
  criticalIncidents,
  closedOnceIncidents,

  taskTypeData,
  taskTable,

  changeTable,

  tasksNextTwoWeeks,
  changeNextTwoWeeks,
  problemData,
  repetitive,
}) => {
  const errorTypes1 = Array.from(new Set(repetitive.map((r) => r.errorType)));
  const formattedData = Object.values(
    repetitive.reduce((acc, item) => {
      if (!acc[item.link]) {
        acc[item.link] = {
          link: item.link,
          total: 0,
        };
      }

      acc[item.link][item.errorType] = item.value;

      // add to total
      acc[item.link].total += item.value;

      return acc;
    }, {}),
  );
  const sortedFormattedData = [...formattedData].sort(
    (a: any, b: any) => b.total - a.total,
  );
  // ================= CLEAN DATA =================
  const cleanChartData = (data: any[]) =>
    (data || [])
      .filter((d) => d?.name !== null && d?.name !== "")
      .map((d) => ({
        name: String(d.name).trim().replace(/\n/g, " ") || "Unknown",
        value: Number(d.value) || 0,
      }));

  // ================= MEMO DATA =================
  // const complaintsData = useMemo(
  //   () => cleanChartData(complaints),
  //   [complaints],
  // );

  // const statusData = useMemo(() => cleanChartData(status), [status]);

  // const categoriesData = useMemo(
  //   () => cleanChartData(categories),
  //   [categories],
  // );

  // const errorTypesData = useMemo(
  //   () => cleanChartData(errorTypes),
  //   [errorTypes],
  // );
  const MAX_PIE_ITEMS = 25;
  const limitPieData = (data: any[]) => {
    if (!data) return [];

    const sorted = [...data]
      .filter((d) => d?.name)
      .sort((a, b) => b.value - a.value);

    if (sorted.length <= MAX_PIE_ITEMS) return sorted;

    const top = sorted.slice(0, MAX_PIE_ITEMS);
    const rest = sorted.slice(MAX_PIE_ITEMS);

    const otherValue = rest.reduce((sum, item) => sum + (item.value || 0), 0);

    return [
      ...top,
      {
        name: "Other",
        value: otherValue,
      },
    ];
  };
  const pptx = new PptxGenJS();
  const captureTableSlide = async (
    pptx: any,
    title: string,
    element: HTMLElement | null,
  ) => {
    if (!element) return;

    const wrapper = document.createElement("div");

    wrapper.style.width = "1280px";
    wrapper.style.background = "#fff";
    wrapper.style.padding = "20px";

    // 🔥 IMPORTANT: FORCE ZOOM (this is what you're missing)
    wrapper.style.transform = "scale(1.4)";
    wrapper.style.transformOrigin = "top left";

    const clone = element.cloneNode(true) as HTMLElement;

    // 🔥 Increase readability BEFORE capture
    clone.querySelectorAll("td, th").forEach((el) => {
      const cell = el as HTMLElement;
      cell.style.fontSize = "14px";
      cell.style.padding = "6px";
      cell.style.whiteSpace = "normal";
    });

    wrapper.appendChild(clone);
    document.body.appendChild(wrapper);

    const canvas = await html2canvas(wrapper, {
      scale: 2,
      useCORS: true,
      backgroundColor: "#ffffff",
    });

    document.body.removeChild(wrapper);

    const imgData = canvas.toDataURL("image/png");

    const slide = pptx.addSlide();

    slide.addText(title, {
      x: 0.5,
      y: 0.2,
      fontSize: 18,
      bold: true,
    });

    // 🔥 IMPORTANT: give it enough space in PPT
    slide.addImage({
      data: imgData,
      x: 0.3,
      y: 0.8,
      w: 9.8,
      h: 5.4,
    });
  };
  const complaintsData = useMemo(
    () => limitPieData(cleanChartData(complaints)),
    [complaints],
  );

  const statusData = useMemo(
    () => limitPieData(cleanChartData(status)),
    [status],
  );

  const categoriesData = useMemo(
    () => limitPieData(cleanChartData(categories)),
    [categories],
  );

  const errorTypesData = useMemo(
    () => limitPieData(cleanChartData(errorTypes)),
    [errorTypes],
  );
  const chunkArray = (data: any[], size: number) => {
    const chunks = [];
    for (let i = 0; i < data.length; i += size) {
      chunks.push(data.slice(i, i + size));
    }
    return chunks;
  };
  // ================= INCIDENT REFS =================
  const complaintsRef = useRef<HTMLDivElement>(null);
  const statusRef = useRef<HTMLDivElement>(null);
  const categoriesRef = useRef<HTMLDivElement>(null);
  const errorTypesRef = useRef<HTMLDivElement>(null);
  const networkRef = useRef<HTMLDivElement>(null);
  const repetitiveRef = useRef<HTMLDivElement>(null);

  const openIncidentsRef = useRef<HTMLDivElement>(null);
  const criticalIncidentsRef = useRef<HTMLDivElement>(null);
  const closedOnceIncidentsRef = useRef<HTMLDivElement>(null);

  // ================= TASK REFS =================
  const taskBarRef = useRef<HTMLDivElement>(null);
  const taskTableRef = useRef<HTMLDivElement>(null);
  const taskNextWeeksRef = useRef<HTMLDivElement>(null);

  // ================= CHANGE REFS =================
  const changeTableRef = useRef<HTMLDivElement>(null);
  const changeNextWeeksRef = useRef<HTMLDivElement>(null);
  const problemDataRef = useRef<HTMLDivElement>(null);
  const complaintsTotal = complaintsData.reduce(
    (sum, item) => sum + (item.value || 0),
    0,
  );
  const renderLinksLabel = (props: any) => {
    const { x, y, value } = props;

    if (!value) return <></>;

    return (
      <text
        x={x}
        y={y - 5}
        textAnchor="middle"
        fontSize={12}
        fontWeight={600}
        fill="#82ca9d" // 🟢 green
      >
        {value}
      </text>
    );
  };
  const renderIncidentsLabel = (props: any) => {
    const { x, y, value } = props;

    if (!value) return <></>;

    return (
      <text
        x={x}
        y={y - 5}
        textAnchor="middle"
        fontSize={12}
        fontWeight={600}
        fill="#8884d8" // 🟣 purple
      >
        {value}
      </text>
    );
  };
  // ================= PPT EXPORT =================
  const exportPPT = async () => {
    const pptx = new PptxGenJS();

    pptx.layout = "LAYOUT_WIDE";

    const captureSlide = async (title: string, element: HTMLElement | null) => {
      if (!element) return;

      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        backgroundColor: "#ffffff",
        windowWidth: element.scrollWidth,
      });

      const imgData = canvas.toDataURL("image/png");

      const slide = pptx.addSlide();

      slide.addText(title, {
        x: 0.5,
        y: 0.2,
        fontSize: 20,
        bold: true,
      });

      slide.addImage({
        data: imgData,
        x: 0,
        y: 0.8,
        w: "100%",
        h: "80%",
      });
    };

    // ================= INCIDENT =================
    if (module === "incident") {
      await captureSlide("Complaints", complaintsRef.current);

      await captureSlide("Status", statusRef.current);

      await captureSlide("Categories", categoriesRef.current);

      await captureSlide("Error Types", errorTypesRef.current);
      await captureSlide("Repetitive Incidents", repetitiveRef.current);

      await captureSlide("Network Incidents", networkRef.current);

      await captureSlide("Open Incidents", openIncidentsRef.current);

      await captureSlide("Critical Incidents", criticalIncidentsRef.current);

      await captureSlide(
        "Closed Once Incidents",
        closedOnceIncidentsRef.current,
      );
    }

    // ================= TASK =================
    if (module === "task") {
      await captureSlide("Task Type Statistics", taskBarRef.current);
      await captureTableSlide(pptx, "Task Table", taskTableRef.current);

      await captureTableSlide(
        pptx,
        "Tasks Next Month",
        taskNextWeeksRef.current,
      );
    }

    // ================= CHANGE =================
    if (module === "change") {
      await captureSlide("Change Table", changeTableRef.current);

      await captureSlide("Changes Next 2 Weeks", changeNextWeeksRef.current);
    }
    if (module === "problem") {
      await captureTableSlide(pptx, "Problem Table", problemDataRef.current);
    }
    await pptx.writeFile({
      fileName: `${module}-report.pptx`,
    });
  };

  // ================= JSX =================
  return (
    <div className="reporting-container">
      <h2 className="reporting-title">Reporting Dashboard</h2>

      {/* ================= FILTERS ================= */}
      <div className="reporting-filters">
        <ApiDropdown
          api="affected-customers"
          labelKey="customername"
          valueKey="customername"
          label="Customer"
          required
          isMulti
          onChange={(val: any) => setCustomers(val)}
        />

        <div className="filter-item">
          <label>Period</label>

          <div className="date-container">
            <input
              type="date"
              value={periodFrom}
              onChange={(e) => setPeriodFrom(e.target.value)}
            />

            <span>to</span>

            <input
              type="date"
              value={periodTo}
              onChange={(e) => setPeriodTo(e.target.value)}
            />
          </div>
        </div>

        <div className="filter-item">
          <label>Module</label>

          <select value={module} onChange={(e) => setModule(e.target.value)}>
            <option value="">Select module</option>

            {modules.map((m) => (
              <option key={m.value} value={m.value}>
                {m.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* ================= BUTTONS ================= */}
      <button
        className="generate-btn"
        onClick={handleGenerate}
        disabled={loading}
      >
        {loading ? "Generating..." : "Generate Report"}
      </button>

      <button className="generate-btn" onClick={exportPPT}>
        Export PPT
      </button>

      {/* ========================================================= */}
      {/* ================= INCIDENT MODULE ======================= */}
      {/* ========================================================= */}

      {module === "incident" && (
        <>
          {/* ================= COMPLAINTS ================= */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(2, 1fr)",
              gap: "20px",
              alignItems: "start",
            }}
          >
            {complaintsData.length > 0 && (
              <div
                ref={complaintsRef}
                className="ppt-section"
                style={{
                  background: "#fff",
                  padding: "20px",
                  marginBottom: "30px",
                  width: "1100px",
                  textAlign: "center",
                  position: "relative", // IMPORTANT
                  fontSize: "15px",
                }}
              >
                <h3>Customer complaints vs. Detected incidents</h3>

                <PieChart width={1000} height={500}>
                  <Pie
                    data={complaintsData}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={170}
                    labelLine={true}
                    label={({ name, value, percent }) =>
                      `${name}: ${value} (${((percent || 0) * 100).toFixed(0)}%)`
                    }
                  >
                    {complaintsData.map((_, i) => (
                      <Cell key={i} fill={COLORS[i % COLORS.length]} />
                    ))}
                  </Pie>

                  <Tooltip />
                  <Legend
                    layout="horizontal"
                    verticalAlign="bottom"
                    align="center"
                  />
                </PieChart>

                {/* ✅ TOTAL bottom-right */}
                <div
                  style={{
                    position: "absolute",
                    bottom: "15px",
                    right: "20px",
                    fontSize: "16px",
                    fontWeight: "bold",
                    background: "#fff",
                    padding: "6px 10px",
                    borderRadius: "6px",
                    boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
                  }}
                >
                  Total Number of incidents: {complaintsTotal}
                </div>
              </div>
            )}

            {/* ================= STATUS ================= */}
            {/* ================= STATUS ================= */}
            {statusData.length > 0 && (
              <div
                ref={statusRef}
                className="ppt-section"
                style={{
                  background: "#fff",
                  padding: "20px",
                  marginBottom: "30px",
                  width: "1100px",
                  textAlign: "center",
                  position: "relative", // IMPORTANT
                  fontSize: "15px",
                }}
              >
                <h3>Incidents status</h3>

                <PieChart width={1000} height={500}>
                  <Pie
                    data={statusData}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={170}
                    labelLine={true}
                    label={({ name, value, percent }) =>
                      `${name}: ${value} (${((percent || 0) * 100).toFixed(0)}%)`
                    }
                  >
                    {statusData.map((_, i) => (
                      <Cell key={i} fill={COLORS[i % COLORS.length]} />
                    ))}
                  </Pie>

                  <Tooltip />

                  <Legend
                    layout="horizontal"
                    verticalAlign="bottom"
                    align="center"
                  />
                </PieChart>
              </div>
            )}

            {/* ================= CATEGORIES ================= */}
            {categoriesData.length > 0 && (
              <div
                ref={categoriesRef}
                className="ppt-section"
                style={{
                  background: "#fff",
                  padding: "20px",
                  marginBottom: "30px",
                  textAlign: "center",
                  position: "relative", // IMPORTANT
                  width: "1100px",
                  fontSize: "15px",
                }}
              >
                <h3>Categories of incidents</h3>

                <PieChart width={1000} height={500}>
                  <Pie
                    data={categoriesData}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={170}
                    labelLine={true}
                    label={({ name, value, percent }) =>
                      `${name}: ${value} (${((percent || 0) * 100).toFixed(0)}%)`
                    }
                  >
                    {categoriesData.map((_, i) => (
                      <Cell key={i} fill={COLORS[i % COLORS.length]} />
                    ))}
                  </Pie>

                  <Tooltip />

                  <Legend
                    layout="horizontal"
                    verticalAlign="bottom"
                    align="center"
                  />
                </PieChart>
              </div>
            )}

            {/* ================= ERROR TYPES ================= */}
            {errorTypesData.length > 0 && (
              <div
                ref={errorTypesRef}
                className="ppt-section"
                style={{
                  background: "#fff",
                  textAlign: "center",
                  position: "relative", // IMPORTANT
                  padding: "20px",
                  marginBottom: "30px",
                  width: "1100px",
                  fontSize: "15px",
                }}
              >
                <h3>Root cause of incidents</h3>

                <PieChart width={1000} height={500}>
                  <Pie
                    data={errorTypesData}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={170}
                    labelLine={true}
                    label={({ name, value, percent }) =>
                      `${name}: ${value} (${((percent || 0) * 100).toFixed(0)}%)`
                    }
                  >
                    {errorTypesData.map((_, i) => (
                      <Cell key={i} fill={COLORS[i % COLORS.length]} />
                    ))}
                  </Pie>

                  <Tooltip />

                  <Legend
                    layout="horizontal"
                    verticalAlign="bottom"
                    align="center"
                  />
                </PieChart>
              </div>
            )}
          </div>
          {/* ================= NETWORK BAR ================= */}
          {/* ================= NETWORK BAR ================= */}
          {networkData.length > 0 && (
            <div
              ref={networkRef}
              className="ppt-section"
              style={{
                background: "#fff",
                padding: "20px",
                marginBottom: "30px",
                width: "98%",
              }}
            >
              <h3
                style={{
                  textAlign: "center",
                  marginBottom: "10px",
                  fontSize: "18px",
                  fontWeight: "bold",
                }}
              >
                📊 Total number of incidents per network
              </h3>

              <BarChart
                width={1500}
                height={800}
                data={networkData}
                margin={{
                  top: 20,
                  right: 30,
                  left: 20,
                  bottom: 150, // keep this here
                }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="name"
                  angle={-40}
                  interval={0}
                  height={100}
                  tick={{ textAnchor: "end" }}
                />
                <YAxis />
                <Tooltip />
                <Legend
                  layout="vertical"
                  verticalAlign="middle"
                  align="right"
                />

                {/* Links bar */}
                <Bar
                  dataKey="links"
                  name="Links"
                  fill="#82ca9d"
                  label={renderLinksLabel}
                />

                <Bar
                  dataKey="incidents"
                  name="Incidents"
                  fill="#8884d8"
                  label={renderIncidentsLabel}
                />
              </BarChart>
            </div>
          )}
          {repetitive.length > 0 && (
            <div
              ref={repetitiveRef}
              className="ppt-section"
              style={{
                background: "#fff",
                padding: "20px",
                marginBottom: "30px",
                width: "98%",
              }}
            >
              <h3
                style={{
                  textAlign: "center",
                  marginBottom: "10px",
                  fontSize: "18px",
                  fontWeight: "bold",
                }}
              >
                Repetitive Incidents by Link
              </h3>

              <BarChart
                width={1500}
                height={800}
                data={sortedFormattedData}
                barSize={45}
                margin={{
                  top: 20,
                  right: 30,
                  left: 20,
                  bottom: 100,
                }}
              >
                <CartesianGrid strokeDasharray="3 3" />

                <XAxis
                  dataKey="link"
                  angle={-35}
                  textAnchor="end"
                  interval={0}
                  height={100}
                />

                <YAxis />
                <Tooltip />
                <Legend
                  layout="vertical"
                  verticalAlign="middle"
                  align="right"
                />

                {errorTypes1.map((type, i) => (
                  <Bar
                    key={type}
                    dataKey={type}
                    stackId="a"
                    name={type}
                    barSize={45}
                    fill={COLORS[i % COLORS.length]}
                  />
                ))}

                {/* ✅ TOTAL ON TOP OF EACH STACK */}
                <Bar dataKey="total" fill="transparent">
                  <LabelList
                    dataKey="total"
                    position="top"
                    fill="black"
                    style={{
                      fill: "black",
                      fontSize: 12,
                      fontWeight: "bold",
                    }}
                  />
                </Bar>
              </BarChart>
            </div>
          )}
          {/* ================= OPEN INCIDENTS ================= */}
          <div ref={openIncidentsRef}>
            <h3>🟢 Open Incidents (Non-Critical)</h3>

            <table className="report-table small-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Link</th>
                  <th>Summary</th>
                  <th>Start</th>
                  <th>Category</th>
                </tr>
              </thead>

              <tbody>
                {openIncidents.length > 0 ? (
                  openIncidents.map((i, idx) => (
                    <tr key={idx}>
                      <td>{i.incidentId}</td>
                      <td>{i.link}</td>
                      <td>{i.summary}</td>
                      <td>{i.startDateTime}</td>
                      <td>{i.category}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={5} style={{ textAlign: "center" }}>
                      No data available
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <div ref={criticalIncidentsRef}>
            <h3>🔴 Incidents (open and closed) having priority critical</h3>

            <table className="report-table small-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Network</th>
                  <th>Link</th>
                  <th>Summary</th>
                  <th>Start</th>
                  <th>Close</th>
                  <th>Error</th>
                  <th>Category</th>
                  <th>Resolution</th>
                </tr>
              </thead>

              <tbody>
                {criticalIncidents.length > 0 ? (
                  criticalIncidents.map((i, idx) => (
                    <tr key={idx}>
                      <td>{i.incidentId}</td>
                      <td>{i.network}</td>
                      <td>{i.link}</td>
                      <td>{i.summary}</td>
                      <td>{i.startDateTime}</td>
                      <td>{i.closeDateTime}</td>
                      <td>{i.errorType}</td>
                      <td>{i.category}</td>
                      <td>{i.resolution}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={10} style={{ textAlign: "center" }}>
                      No data available
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {/* ================= CLOSED ONCE ================= */}
          <div ref={closedOnceIncidentsRef}>
            <h3>🟡 Non-repetitive Closed incidents</h3>

            <table className="report-table small-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Network</th>
                  <th>Link</th>
                  <th>Summary</th>
                  <th>Start</th>
                  <th>Close</th>
                  <th>Error</th>
                  <th>Category</th>
                  <th>Resolution</th>
                </tr>
              </thead>

              <tbody>
                {closedOnceIncidents.length > 0 ? (
                  closedOnceIncidents.map((i, idx) => (
                    <tr key={idx}>
                      <td>{i.incidentId}</td>
                      <td>{i.network}</td>
                      <td>{i.link}</td>
                      <td>{i.summary}</td>
                      <td>{i.startDateTime}</td>
                      <td>{i.closeDateTime}</td>
                      <td>{i.errorType}</td>
                      <td>{i.category}</td>
                      <td>{i.resolution}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={10} style={{ textAlign: "center" }}>
                      No data available
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* ========================================================= */}
      {/* ================= TASK MODULE =========================== */}
      {/* ========================================================= */}

      {module === "task" && (
        <>
          {/* ================= TASK BAR ================= */}
          {taskTypeData.length > 0 && (
            <div ref={taskBarRef}>
              <h3>Type of tasks</h3>

              <BarChart
                width={1000}
                height={800}
                data={taskTypeData}
                margin={{
                  top: 20,
                  right: 30,
                  left: 20,
                  bottom: 100,
                }}
              >
                <CartesianGrid strokeDasharray="1 1" />

                <XAxis
                  dataKey="name"
                  interval={0}
                  angle={-45}
                  textAnchor="end"
                  height={80}
                />

                <YAxis />

                <Tooltip />

                <Legend
                  layout="vertical"
                  verticalAlign="middle"
                  align="right"
                />

                <Bar dataKey="tasks" fill="#7861c6">
                  <LabelList dataKey="tasks" position="top" />
                </Bar>
              </BarChart>
            </div>
          )}

          {/* ================= TASK TABLE ================= */}
          <div ref={taskTableRef}>
            <h3>Preventive/Corrective Activities</h3>

            <table className="report-table small-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Network</th>
                  <th>Antenna</th>
                  <th>Link</th>
                  <th>Summary</th>
                  <th>Start</th>
                  <th>End</th>
                  <th>Type</th>
                  <th>Phase</th>
                  <th>Closure</th>
                </tr>
              </thead>{" "}
              <tbody>
                {taskTable.length > 0 ? (
                  taskTable.map((t, idx) => (
                    <tr key={idx}>
                      <td>{t[0]}</td>
                      <td>{t[1]}</td>
                      <td>{t[2]}</td>
                      <td>{t[3]}</td>
                      <td>{t[4]}</td>
                      <td>{t[5]}</td>
                      <td>{t[6]}</td>
                      <td>{t[7]}</td>
                      <td>{t[8]}</td>
                      <td>{t[9]}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={10} style={{ textAlign: "center" }}>
                      No data available
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {/* ================= TASK NEXT 2 WEEKS ================= */}
          <div ref={taskNextWeeksRef}>
            <h3>Planned Activities For The Coming Month</h3>

            <table className="report-table small-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Network</th>
                  <th>Antenna</th>
                  <th>Link</th>
                  <th>Summary</th>
                  <th>Start</th>
                  <th>End</th>
                  <th>Type</th>
                  <th>Phase</th>
                </tr>
              </thead>{" "}
              <tbody>
                {tasksNextTwoWeeks.length > 0 ? (
                  tasksNextTwoWeeks.map((t, idx) => (
                    <tr key={idx}>
                      <td>{t[0]}</td>
                      <td>{t[1]}</td>
                      <td>{t[2]}</td>
                      <td>{t[3]}</td>
                      <td>{t[4]}</td>
                      <td>{t[5]}</td>
                      <td>{t[6]}</td>
                      <td>{t[7]}</td>
                      <td>{t[8]}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={9} style={{ textAlign: "center" }}>
                      No data available
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* ========================================================= */}
      {/* ================= CHANGE MODULE ========================= */}
      {/* ========================================================= */}

      {module === "change" && (
        <>
          {/* ================= CHANGE TABLE ================= */}
          <div ref={changeTableRef}>
            <h3>📋 Change Activities </h3>

            <table className="report-table small-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Network</th>
                  <th>Antenna</th>
                  <th>Link</th>
                  <th>Summary</th>
                  <th>Start</th>
                  <th>End</th>
                  <th>Type</th>
                  <th>Phase</th>
                  <th>Closure</th>
                </tr>
              </thead>
              <tbody>
                {changeTable.length > 0 ? (
                  changeTable.map((t, idx) => (
                    <tr key={idx}>
                      <td>{t[0]}</td>
                      <td>{t[1]}</td>
                      <td>{t[2]}</td>
                      <td>{t[3]}</td>
                      <td>{t[4]}</td>
                      <td>{t[5]}</td>
                      <td>{t[6]}</td>
                      <td>{t[7]}</td>
                      <td>{t[8]}</td>
                      <td>{t[9]}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={10} style={{ textAlign: "center" }}>
                      No data available
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {/* ================= CHANGE NEXT 2 WEEKS ================= */}
          <div ref={changeNextWeeksRef}>
            <h3>📅 Planned Changes for the coming Month</h3>

            <table className="report-table small-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Network</th>
                  <th>Antenna</th>
                  <th>Link</th>
                  <th>Summary</th>
                  <th>Start</th>
                  <th>End</th>
                  <th>Type</th>
                  <th>Phase</th>
                </tr>
              </thead>{" "}
              <tbody>
                {changeNextTwoWeeks.length > 0 ? (
                  changeNextTwoWeeks.map((t, idx) => (
                    <tr key={idx}>
                      <td>{t[0]}</td>
                      <td>{t[1]}</td>
                      <td>{t[2]}</td>
                      <td>{t[3]}</td>
                      <td>{t[4]}</td>
                      <td>{t[5]}</td>
                      <td>{t[6]}</td>
                      <td>{t[7]}</td>
                      <td>{t[8]}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={9} style={{ textAlign: "center" }}>
                      No data available
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {module === "problem" && (
        <>
          {/* ================= CHANGE TABLE ================= */}
          <div ref={problemDataRef}>
            <h3>📋 Problem (Filtered)</h3>

            <table className="report-table small-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Network</th>

                  <th>Link</th>
                  <th>Summary</th>
                  <th>Start</th>
                  <th>End</th>
                  <th>Solution</th>
                  <th>Error</th>
                  <th>Closure</th>
                </tr>
              </thead>
              <tbody>
                {problemData.length > 0 ? (
                  problemData.map((t, idx) => (
                    <tr key={idx}>
                      <td>{t[0]}</td>
                      <td>{t[1]}</td>
                      <td>{t[2]}</td>
                      <td>{t[3]}</td>
                      <td>{t[4]}</td>
                      <td>{t[5]}</td>
                      <td>{t[6]}</td>
                      <td>{t[7]}</td>
                      <td>{t[8]}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={9} style={{ textAlign: "center" }}>
                      No data available
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

export default ReportingView;
